import Head from 'next/head'
import { useRouter } from 'next/router'
import { InventoryItem, Venue } from 'types/model'

import { imageOptimize } from '@utils/images'
import { env } from '@lib/env'
import { useMemo } from 'react'
import { formatPrice } from '@utils/strings'

export function InventoryMeta({
  venue,
  inventory,
}: { venue: Venue; inventory: InventoryItem }) {
  const router = useRouter()
  const pageTitle = `Confirm your reservation at ${venue.name}`
  const description = `${inventory.name} | ${formatPrice(inventory.price, {
    currency: venue.currencyCode,
    languageCode: venue.languageCode,
  })}`

  const image = useMemo(
    () =>
      imageOptimize(venue.primaryImage?.filename, {
        w: '1200',
        h: '627',
        q: '20',
        dpr: '0.5',
        fm: 'webp',
        fit: 'fill',
        fill: 'blur',
      }),
    [venue]
  )

  const keywords = useMemo(
    () => [
      'Nightlife',
      'Night life',
      'Venue',
      'Late night',
      'Club',
      'Tablelist',
      'Tablelist Pro',
      venue.name,

      inventory.name,
      inventory.description,
      ...(venue.location.address
        ? [
            venue.location.address.city,
            venue.location.address.state,
            venue.location.address.zip,
          ]
        : []),
    ],
    [inventory.description, inventory.name, venue.location.address, venue.name]
  )
  const url = useMemo(
    () => `${env.domain}/${router.asPath.split('?')[0]}`,
    [router.asPath]
  )

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name='description' content={description} />
      <meta name='keywords' content={keywords.join(', ')} />
      <meta name='author' content={venue.name} />

      <meta property='og:type' content='website' />
      <meta property='og:title' content={pageTitle} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={image} />
      <meta property='og:url' content={url} />

      <meta property='twitter:card' content='summary_large_image' />
      <meta property='twitter:url' content={url} />
      <meta property='twitter:title' content={pageTitle} />
      <meta property='twitter:description' content={description} />
      <meta property='twitter:image' content={image} />
    </Head>
  )
}
